/**
 * AssistantDockProvider — context for the global assistant dock (design doc §6).
 *
 * Provides screen-aware placeholder copy and the current route context to the
 * GlobalAssistantDock, plus an imperative open/close API so any screen can pop
 * the dock open (e.g. a "Ask BEAM" affordance). The dock is mounted once at the
 * root (inside HelpProvider) so a single provider serves every authed route.
 *
 * Screen-awareness is derived from the active Expo Router pathname (usePathname),
 * so the placeholder changes as the user navigates without each screen wiring it.
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { usePathname } from 'expo-router';

interface AssistantDockContextValue {
  /** Whether the expanded dock is open. */
  open: boolean;
  /** Open the expanded dock (optionally seeding the input). */
  openDock: (seed?: string) => void;
  /** Collapse the dock back to the FAB/pill. */
  closeDock: () => void;
  /** Screen-aware placeholder text for the input, derived from the active route. */
  placeholder: string;
  /** The active route pathname, passed to assistantAct as context.route. */
  route: string;
  /** Initial text to seed the field when opened via openDock(seed). */
  seedText: string;
  /** Clear the one-shot seed after the dock consumes it. */
  consumeSeed: () => void;
}

const AssistantDockContext = createContext<AssistantDockContextValue | null>(null);

/**
 * Map an active pathname to a concrete example placeholder. Falls back to a
 * generic, action-forward prompt so the copy is never empty or "type a command".
 */
function placeholderForRoute(pathname: string): string {
  const p = pathname.toLowerCase();
  if (p.includes('/estimates') || p.includes('/estimator')) {
    return "e.g. 'add 200ft of trim to the Smith estimate'";
  }
  if (p.includes('/calendar') || p.includes('/schedule')) {
    return "e.g. 'schedule a site visit Friday 9am'";
  }
  if (p.includes('/tasks')) {
    return "e.g. 'assign the framing task to Mateo'";
  }
  if (p.includes('/customers')) {
    return "e.g. 'log a $142 receipt from Home Depot'";
  }
  if (p.includes('/invoices')) {
    return "e.g. 'create an estimate for the Garcia kitchen'";
  }
  if (p.includes('/queue') || p.includes('/review') || p.includes('/capture')) {
    return "e.g. 'log a $142 receipt from Home Depot'";
  }
  return "e.g. 'create a task to order tile by Friday'";
}

export function AssistantDockProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [seedText, setSeedText] = useState('');

  const openDock = useCallback((seed?: string) => {
    if (seed) setSeedText(seed);
    setOpen(true);
  }, []);

  const closeDock = useCallback(() => setOpen(false), []);
  const consumeSeed = useCallback(() => setSeedText(''), []);

  const placeholder = useMemo(() => placeholderForRoute(pathname ?? ''), [pathname]);

  const value = useMemo<AssistantDockContextValue>(
    () => ({
      open,
      openDock,
      closeDock,
      placeholder,
      route: pathname ?? '',
      seedText,
      consumeSeed,
    }),
    [open, openDock, closeDock, placeholder, pathname, seedText, consumeSeed],
  );

  return <AssistantDockContext.Provider value={value}>{children}</AssistantDockContext.Provider>;
}

/** Access the dock context. Returns null when no provider is mounted (unauthenticated). */
export function useAssistantDock(): AssistantDockContextValue | null {
  return useContext(AssistantDockContext);
}
